import { skipToken, useQuery } from '@tanstack/react-query';

import { apiClient } from 'core/api/globals';
import { useValidatedParam } from 'core/hooks';

import { AuthenticationScheme, useAuthentication } from 'features/auth';
import { useSessionLocation } from 'features/location';

export function useQueryCurrentSettings() {
  // Note: This hook is not a good example for how to define general-purpose query hook.  It has to handle quite a bit of corner cases in
  // ways that are not super obvious (particularly the "linkId" and "activeScheme" logic).  However there are several places throughout
  // the app that need access to the current settings so it's a bit difficult to eliminate the need for this hook.  Just be cautious what
  // new edge cases are added here.  Ideally we should strive to make this hook more simple instead of more complex.

  const { activeScheme } = useAuthentication();
  const { sessionLocation } = useSessionLocation();

  const shareLinkId = useValidatedParam('linkId', 'guid', false);

  // Currently all share routes have a "linkId" parameter.  Protect against a developer adding a share route without a "linkId" paramter.  If that does become necessary then this hook will likely need to be refactored to accommodate.
  // However, only throw this error if we're actually on a share route (URL contains '/share/')
  const isOnShareRoute = window.location.pathname.includes('/share/');
  if (activeScheme === AuthenticationScheme.SHARE && shareLinkId == null && isOnShareRoute) {
    throw new Error('Unable to fetch settings because the share link id was expected as a route parameter "linkId".');
  }

  /* The logic for determining whether we fetch settings for OIDC, Share, or anonymous is a bit tricky.  Here is the priority (highest to lowest):
  1) Fetch settings for a share link when there is a "linkId" parameter.
    - Some share routes can be accessed by anonymous users users (particularly the share landing page to enforce the HIPAA agreement).
    - It's possible and expected for users that are already logged in via OIDC to access a share link while they are logged in.
    - Both scenarios make it so that we can't safely reference the "activeScheme" here to determine what settings to fetch.  For shares we are going to assert that if the "linkId" parameter is present, then we are fetching settings for a share link.
  2) Fetch settings for the current user (OIDC).
    - We can key off the "activeScheme" here to determine what settings to fetch, as long as we don't have a "linkId" parameter.
  3) Fetch settings for anonymous users (OIDC).
    - We can key off the "activeScheme" here to determine what settings to fetch, as long as we don't have a "linkId" parameter.
  4) There isn't a "linkId" parameter and the "activeScheme" is undefined (it's still initializing).
    - Don't fetch anything - wait for initialization to complete.
  */
  const mode =
    shareLinkId != null
      ? 'share'
      : activeScheme === AuthenticationScheme.OIDC
        ? 'oidc'
        : activeScheme === AuthenticationScheme.ANONYMOUS
          ? 'anonymous'
          : undefined;

  const results = useQuery({
    queryKey:
      mode === 'oidc'
        ? ['settings', 'for-current-user', 'session-location', sessionLocation?.id ?? null]
        : mode === 'share'
          ? ['settings', 'for-share-link-id', shareLinkId] // Don't include the session location id for shares because the backend will already know and reference the location settings when retrieving settings for a share link.
          : ['settings'],
    queryFn:
      mode == null
        ? skipToken
        : () =>
            apiClient.settingsClient.getSettings(
              mode === 'oidc' ? true : false,
              shareLinkId ?? null,
              mode === 'oidc' ? (sessionLocation?.id ?? null) : null, // The session location id is only needed for OIDC auth because that is the only scenario where the user is given a choice of locations.  Share links are always associated with a specific location on the backend, and anonymous users don't have any access to a list of locations.
              mode === 'oidc' ? 'msal-optional' : null, // Don't use authentication for share links because that needs to be parameterized via the "shareLinkId" parameter.  This protects against odd scenarios such as when the user accesses multiple share links within the same session - we don't want to fetch settings for the wrong share link.
            ),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return results;
}
